import { supabaseClient } from "@/lib/supabase";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/utils/auth";
import { filterStaffBySecretaryOwnership, selectLinkedSecretaryIds } from "./filterStaffBySecretaryOwnership";
import { selectProfilesByRoles } from "./selectProfilesByRoles";

/** Default staff picker roles (clinician workflows, favourites, etc.). */
export const CLINICAL_STAFF_ROLES = ["CLINICIAN", "SECRETARY"] as const;

/** Share Template may target managers so they can attach copies on studies. */
export const SHARE_TEMPLATE_STAFF_ROLES = ["CLINICIAN", "SECRETARY", "MANAGER"] as const;

const selectStaff = async (viewerId: string, roles: readonly string[] = CLINICAL_STAFF_ROLES) => {
    const [data, linkedSecretaryIds, profileResult] = await Promise.all([
        selectProfilesByRoles({ roles }),
        selectLinkedSecretaryIds(viewerId),
        supabaseClient.from("profiles").select("is_super_user, roles").eq("user_id", viewerId).single(),
    ]);
    if (profileResult.error) throw profileResult.error;

    const skipFilter = Boolean(profileResult.data?.is_super_user);
    return filterStaffBySecretaryOwnership(data, linkedSecretaryIds, { skipFilter });
};

export const selectStaffQueryKey = ["select-staff"] as const;

export const useSelectStaff = (roles: readonly string[] = CLINICAL_STAFF_ROLES) => {
    const auth = useAuth();
    return useQuery({
        queryKey: [...selectStaffQueryKey, auth.id, ...roles],
        queryFn: () => selectStaff(auth.id, roles),
        enabled: Boolean(auth.id),
    });
};
